// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import { useMemo } from 'react'
import styles from './LabSpark.module.css'
import { nearestPoint, type Point } from 'lib/dealhunt/audioChart'
import { THIS_COLOR, YOURS_COLOR } from 'lib/owner-notes/curves'
import { BANDS, curveVerdict, type CurveVerdict } from 'lib/owner-notes/deltas'

/** Sparkline frame — 150×56 user units, log 20 Hz → 20 kHz like the PDP charts. */
export const SPARK_W = 150
export const SPARK_H = 56
const PAD_X = 4
const PAD_Y = 6
const L0 = Math.log10(20)
const L1 = Math.log10(20000)

function x(hz: number, w: number): number {
  return PAD_X + ((Math.log10(hz) - L0) / (L1 - L0)) * (w - PAD_X * 2)
}

function extent(sets: Point[][]): { lo: number; hi: number } {
  let lo = Infinity
  let hi = -Infinity
  for (const pts of sets) {
    for (const [, db] of pts) {
      lo = Math.min(lo, db)
      hi = Math.max(hi, db)
    }
  }
  return { lo, hi }
}

interface LabSparkProps {
  label: string
  kind: 'frequency' | 'attenuation'
  /** The declared pair (orange). Null: the declared product was never lab-tested — this
   *  card's curve alone, no gap, no verdict, no band tint (ticket "Versus yours without
   *  lab data", 2026-09-09). */
  mine: Point[] | null
  /** This card (blue). */
  theirs: Point[]
  productName: string
  declaredName: string
  /** Present: the sparkline is a button that opens the lab dialog on this chart. */
  onOpen?: () => void
  /** The drawing frame in user units. Defaults to the category card's; the guide's roster row
   *  is wider and shorter (its column is the deal row's 270px price column). Only the frame
   *  changes — stroke widths, joins, the band tint and the enlarge mark are the same. */
  size?: { w: number; h: number }
  /** False: no caption line. For a surface that has already said the verdict in words — the
   *  roster line leads with the same band ("7 dB louder in the bass"). */
  caption?: boolean
}

/**
 * One "versus yours" sparkline: both curves on a shared auto-scaled dB axis, the gap between
 * them shaded green where this product beats the declared pair and red where it trails
 * (attenuation only — on frequency response louder is a difference, not a verdict, so the gap
 * stays neutral), and the one band-averaged sentence `curveVerdict` derives. Without a
 * declared curve it is the card's own fingerprint in blue, captioned "this card · lab".
 */
const LabSpark: React.VFC<LabSparkProps> = ({
  label,
  kind,
  mine,
  theirs,
  productName,
  declaredName,
  onOpen,
  size,
  caption = true,
}) => {
  const w = size?.w ?? SPARK_W
  const h = size?.h ?? SPARK_H
  // Everything derived from the two curves is memoized on them: the nearest-sample fill is
  // O(N·M) and the verdict probes both curves 144 times, and a card re-renders on plenty
  // that is not a new pair (filters drawer, a pin, the compare drawer).
  const geometry = useMemo(() => {
    const ext = extent(mine ? [mine, theirs] : [theirs])
    const lo = kind === 'attenuation' ? Math.min(0, ext.lo) : ext.lo
    const pad = Math.max(1, (ext.hi - lo) * 0.1)
    const top = ext.hi + pad
    const bottom = lo - pad
    const y = (db: number): number => PAD_Y + ((top - db) / (top - bottom)) * (h - PAD_Y * 2)
    const path = (pts: Point[]): string =>
      pts
        .filter(([hz]) => hz >= 20 && hz <= 20000)
        .map(([hz, db], i) => `${i ? 'L' : 'M'}${x(hz, w).toFixed(1)} ${y(db).toFixed(1)}`)
        .join(' ')

    // Shaded gap: walk THIS product's samples, read the declared pair's nearest sample at each,
    // and cut the fill wherever the sign of the difference flips.
    const segments: Array<{ better: boolean; d: string }> = []
    if (kind === 'attenuation' && mine) {
      const theirsIn = theirs.filter(([hz]) => hz >= 20 && hz <= 20000)
      let cur: { better: boolean; fwd: string[]; back: string[] } | null = null
      const flush = (): void => {
        if (cur && cur.fwd.length > 2) {
          segments.push({
            better: cur.better,
            d: `M${cur.fwd.join(' L')} L${cur.back.join(' L')} Z`,
          })
        }
        cur = null
      }
      for (const [hz, db] of theirsIn) {
        const nearest = nearestPoint(mine, hz)
        const better = db > nearest[1]
        if (!cur || cur.better !== better) {
          flush()
          cur = { better, fwd: [], back: [] }
        }
        cur.fwd.push(`${x(hz, w).toFixed(1)} ${y(db).toFixed(1)}`)
        cur.back.unshift(`${x(hz, w).toFixed(1)} ${y(nearest[1]).toFixed(1)}`)
      }
      flush()
    }

    const verdict: CurveVerdict | null = mine ? curveVerdict(mine, theirs, kind) : null
    return { top, bottom, y, path, segments, verdict }
  }, [mine, theirs, kind, w, h])
  const { top, bottom, y, path, segments, verdict } = geometry
  const zeroY = kind === 'attenuation' || (0 >= bottom && 0 <= top) ? y(0) : null
  // The band the sentence averages over, tinted so "+6 dB bass" has a place to point at.
  const band = verdict?.band ? BANDS.find(b => b.name === verdict.band) ?? null : null
  const chartLabel = mine
    ? `${label}: ${productName} versus ${declaredName}${verdict ? `, ${verdict.text}` : ''}`
    : `${label}: ${productName} alone — your ${declaredName} was not lab-tested`

  const chart = (
    <svg className={styles.chart} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={chartLabel}>
      {band && (
        <rect
          className={styles.band}
          x={x(band.lo, w)}
          y={PAD_Y}
          width={x(band.hi, w) - x(band.lo, w)}
          height={h - PAD_Y * 2}
        />
      )}
      {[100, 1000, 10000].map(hz => (
        <line
          key={hz}
          className={styles.grid}
          x1={x(hz, w)}
          y1={PAD_Y}
          x2={x(hz, w)}
          y2={h - PAD_Y}
        />
      ))}
      {zeroY !== null && (
        <line className={styles.zero} x1={PAD_X} y1={zeroY} x2={w - PAD_X} y2={zeroY} />
      )}
      {segments.map((s, i) => (
        <path
          // eslint-disable-next-line react/no-array-index-key -- segments are positional
          key={i}
          d={s.d}
          className={s.better ? styles.gapUp : styles.gapDown}
        />
      ))}
      {mine && (
        <path
          d={path(mine)}
          fill="none"
          stroke={YOURS_COLOR}
          strokeWidth="1.8"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
      <path
        d={path(theirs)}
        fill="none"
        stroke={THIS_COLOR}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )

  return (
    <div className={styles.root}>
      {onOpen ? (
        <button
          type="button"
          className={styles.open}
          aria-label={`Enlarge — ${chartLabel}`}
          onClick={onOpen}
        >
          {chart}
          <span className={styles.enlarge} aria-hidden="true">
            <svg
              viewBox="0 0 12 12"
              width="11"
              height="11"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M7 1.5h3.5V5" />
              <path d="M10.5 1.5 6.5 5.5" />
              <path d="M5 10.5H1.5V7" />
              <path d="M1.5 10.5 5.5 6.5" />
            </svg>
          </span>
        </button>
      ) : (
        chart
      )}
      {caption && (
        <div className={styles.caption}>
          <span className={styles.label}>{label}</span>
          {verdict && (
            <span className={styles[`verdict_${verdict.direction}`] ?? styles.verdict}>
              {verdict.text}
            </span>
          )}
          {!mine && (
            <span className={styles.solo}>
              <span className={styles.soloThis}>{'this card'}</span>
              {' · lab'}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default LabSpark
